/**
 * JobTriggerButtons Component
 *
 * Provides action buttons to manually trigger collection indexing,
 * offer syncing, history fetching, and stats refresh jobs.
 * Displays last run timestamp and detailed information for each job type.
 */

import React, { useState } from 'react';
import { triggerCollectionIndexing, triggerOfferSync, triggerHistoryFetch, triggerStatsRefresh } from '../../services/adminApi';
import type { CollectionDetailResponse } from '../../types/admin';

interface JobTriggerButtonsProps {
  issuer: string;
  taxon: number | null; // null for issuer-wide collections
  jobs: CollectionDetailResponse['jobs'];
  collection: CollectionDetailResponse['collection']; // For stats_refreshed_at timestamp
  onJobTriggered?: () => void; // Callback to refresh collection data
}

export function JobTriggerButtons({ issuer, taxon, jobs, collection, onJobTriggered }: JobTriggerButtonsProps) {
  const [loading, setLoading] = useState<'indexing' | 'offers' | 'history' | 'stats' | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getLastRun = (jobType: 'indexing' | 'offerSync' | 'history'): string => {
    const jobList = jobs[jobType];
    if (!jobList || jobList.length === 0) return 'Never';

    // Get the most recent job
    const latestJob = jobList[0];
    return formatDate(latestJob.queuedAt);
  };

  const getJobStatusBadge = (jobType: 'indexing' | 'offerSync' | 'history') => {
    const jobList = jobs[jobType];
    if (!jobList || jobList.length === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
          No jobs yet
        </span>
      );
    }

    const latestJob = jobList[0];
    const status = latestJob.status;

    // Status badge configurations
    const statusConfig = {
      completed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Completed',
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )
      },
      processing: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'Processing',
        icon: (
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )
      },
      queued: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Queued',
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        )
      },
      failed: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Failed',
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )
      },
      cancelled: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        label: 'Cancelled',
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )
      },
      rate_limited: {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        label: 'Rate Limited',
        icon: (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.queued;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getStatsRefreshStatusBadge = () => {
    if (!collection || !collection.lastStatsRefreshAt) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
          No stats yet
        </span>
      );
    }

    // If stats_refreshed_at is set, show completed badge
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Completed
      </span>
    );
  };

  const handleTriggerIndexing = async () => {
    try {
      setLoading('indexing');
      setMessage(null);

      const response = await triggerCollectionIndexing({ issuer, taxon });

      setMessage({
        type: 'success',
        text: `Collection indexing job created successfully! Job ID: ${response.jobId}`,
      });

      // Refresh collection data after a short delay
      setTimeout(() => {
        onJobTriggered?.();
      }, 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to trigger collection indexing',
      });
    } finally {
      setLoading(null);

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleTriggerOffers = async () => {
    try {
      setLoading('offers');
      setMessage(null);

      const response = await triggerOfferSync({ issuer, taxon });

      setMessage({
        type: 'success',
        text: `Offer sync job created successfully! Job ID: ${response.jobId}`,
      });

      // Refresh collection data after a short delay
      setTimeout(() => {
        onJobTriggered?.();
      }, 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to trigger offer sync',
      });
    } finally {
      setLoading(null);

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleTriggerHistory = async () => {
    try {
      setLoading('history');
      setMessage(null);

      const response = await triggerHistoryFetch({ issuer, taxon });

      setMessage({
        type: 'success',
        text: `History fetch job created successfully! Job ID: ${response.jobId}`,
      });

      // Refresh collection data after a short delay
      setTimeout(() => {
        onJobTriggered?.();
      }, 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to trigger history fetch',
      });
    } finally {
      setLoading(null);

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleTriggerStatsRefresh = async () => {
    try {
      setLoading('stats');
      setMessage(null);

      const response = await triggerStatsRefresh(issuer, taxon);

      setMessage({
        type: 'success',
        text: `Stats refresh triggered successfully! ${response.message}`,
      });

      // Refresh collection data after a short delay
      setTimeout(() => {
        onJobTriggered?.();
      }, 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to trigger stats refresh',
      });
    } finally {
      setLoading(null);

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Manual Job Triggers</h3>

      {/* Explanatory Text */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>For new collections:</strong> Trigger <strong>#1 Collection Indexing</strong> first.
          Once all NFTs have completed metadata processing, the system will automatically trigger
          <strong> #2 Collection History</strong> and <strong>#3 Sync Offers</strong>.
          Use <strong>#4 Stats Refresh</strong> if rarity scores need recalculation.
        </p>
        <p className="text-xs text-blue-700 mt-2">
          Manual triggers are useful for retrying failed jobs or forcing an update.
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Action Buttons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* #1 - Trigger Collection Indexing */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">#1</span>
            {getJobStatusBadge('indexing')}
          </div>
          <button
            onClick={handleTriggerIndexing}
            disabled={loading !== null}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
              loading === 'indexing'
                ? 'bg-blue-400 text-white cursor-wait'
                : loading !== null
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading === 'indexing' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Indexing...
              </span>
            ) : (
              'Collection Indexing'
            )}
          </button>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Last run: <span className="font-medium">{getLastRun('indexing')}</span></span>
          </div>
          <div className="mt-3 bg-blue-50 rounded-lg p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900 mb-1">Step 1: Index Collection</div>
            <ul className="space-y-1">
              <li>• Fetches all NFTs from XRPL ledger (XRPLData API)</li>
              <li>• Queues NFTs for metadata & media processing</li>
              <li>• <strong>Wait for all NFTs to complete metadata fetch</strong></li>
              <li>• Duration: ~1-5 minutes for 100 NFTs</li>
            </ul>
          </div>
        </div>

        {/* #2 - Fetch Collection History */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">#2</span>
            {getJobStatusBadge('history')}
          </div>
          <button
            onClick={handleTriggerHistory}
            disabled={loading !== null}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
              loading === 'history'
                ? 'bg-purple-400 text-white cursor-wait'
                : loading !== null
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {loading === 'history' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Fetching...
              </span>
            ) : (
              'Fetch Collection History'
            )}
          </button>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Last run: <span className="font-medium">{getLastRun('history')}</span></span>
          </div>
          <div className="mt-3 bg-purple-50 rounded-lg p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900 mb-1">Step 2: Fetch History (Auto-triggered)</div>
            <ul className="space-y-1">
              <li>• <strong>⚠️ VERY SLOW:</strong> 1 NFT/minute (Bithomp rate limit)</li>
              <li>• 100 NFTs = ~1.7 hours, 1000 NFTs = ~17 hours</li>
              <li>• Auto-triggered after stats refresh</li>
            </ul>
          </div>
        </div>

        {/* #3 - Sync Offers */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">#3</span>
            {getJobStatusBadge('offerSync')}
          </div>
          <button
            onClick={handleTriggerOffers}
            disabled={loading !== null}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
              loading === 'offers'
                ? 'bg-green-400 text-white cursor-wait'
                : loading !== null
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {loading === 'offers' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Syncing...
              </span>
            ) : (
              'Sync Offers'
            )}
          </button>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Last run: <span className="font-medium">{getLastRun('offerSync')}</span></span>
          </div>
          <div className="mt-3 bg-green-50 rounded-lg p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900 mb-1">Step 3: Sync Offers (Auto-triggered)</div>
            <ul className="space-y-1">
              <li>• Fetches active offers from XRPL (XRPLData API)</li>
              <li>• Updates active_offers, recalculates floor price</li>
              <li>• <strong>Fast:</strong> ~30-60 seconds for 100 NFTs</li>
              <li>• Auto-triggered after stats refresh</li>
            </ul>
          </div>
        </div>

        {/* #4 - Refresh Collection Stats */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">#4</span>
            {getStatsRefreshStatusBadge()}
          </div>
          <button
            onClick={handleTriggerStatsRefresh}
            disabled={loading !== null}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
              loading === 'stats'
                ? 'bg-orange-400 text-white cursor-wait'
                : loading !== null
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {loading === 'stats' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Refreshing...
              </span>
            ) : (
              'Refresh Collection Stats'
            )}
          </button>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Last run: <span className="font-medium">{collection && collection.lastStatsRefreshAt ? formatDate(collection.lastStatsRefreshAt) : 'Never'}</span></span>
          </div>
          <div className="mt-3 bg-orange-50 rounded-lg p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900 mb-1">Step 4: Refresh Stats (Optional)</div>
            <ul className="space-y-1">
              <li>• Recalculates rarity scores & trait distributions</li>
              <li>• <strong>Very fast:</strong> 1-2 seconds (database only)</li>
              <li>• Usually not needed (auto-triggered after metadata)</li>
              <li>• Use if rarity scores seem incorrect</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
